import { Component } from '@theme/component';

/**
 * Publica un evento personalizado para el píxel de analítica (GA4) sin romper la página si no existe.
 * @param {string} nombre
 * @param {Record<string, unknown>} [datos]
 */
function publicarEvento(nombre, datos = {}) {
  try {
    window.Shopify?.analytics?.publish?.(nombre, datos);
  } catch {
    // La analítica nunca debe interrumpir la autoevaluación.
  }
}

/** Puntos máximos por pregunta: las respuestas valen de 0 a 3. */
const PUNTOS_MAXIMOS = 3;

/**
 * @typedef {object} Refs
 * @property {HTMLFormElement} formulario
 * @property {HTMLFieldSetElement[]} [grupos]
 * @property {HTMLElement} progreso
 * @property {HTMLElement} aviso
 * @property {HTMLElement} resultado
 * @property {HTMLElement} medidor
 * @property {HTMLElement} puntaje
 * @property {HTMLElement} resultadoTitulo
 * @property {HTMLElement} resultadoTexto
 * @property {HTMLElement} prioridadesBloque
 * @property {HTMLUListElement} prioridades
 * @property {HTMLAnchorElement} cta
 * @property {HTMLScriptElement} rangos
 * @property {HTMLElement} [envio] - Formulario opcional «Recibe tu informe por correo».
 * @property {HTMLElement} [informe] - Informe de una hoja para «Guardar en PDF».
 * @property {HTMLElement} [informeFecha]
 * @property {HTMLElement} [informePuntaje]
 * @property {HTMLElement} [informeTitulo]
 * @property {HTMLElement} [informeTexto]
 * @property {HTMLElement} [informeEscala]
 * @property {HTMLTableSectionElement} [informeAreas]
 * @property {HTMLOListElement} [informePrioridades]
 */

/**
 * @typedef {object} Respuesta
 * @property {string} area
 * @property {number} valor
 * @property {string} texto - La opción elegida, tal como se lee en la pregunta.
 * @property {string} [lectura] - Enlace recomendado si el área sale débil.
 * @property {string} [lecturaTexto]
 */

/** Clase del documento mientras se imprime el informe (la usa oj-informe-autoevaluacion.css). */
const CLASE_IMPRESION = 'oj-imprime-informe';

/**
 * Autoevaluación de control operativo: calcula un puntaje de 0 a 100, muestra la lectura del rango,
 * las áreas más débiles y lleva a la agenda con el puntaje y esas áreas en la URL. Nada sale del
 * navegador salvo el evento anónimo de analítica, a menos que el visitante pida el informe por correo.
 *
 * @extends {Component<Refs>}
 */
class OjAutoevaluacionComponent extends Component {
  requiredRefs = [
    'formulario',
    'progreso',
    'aviso',
    'resultado',
    'medidor',
    'puntaje',
    'resultadoTitulo',
    'resultadoTexto',
    'prioridadesBloque',
    'prioridades',
    'cta',
    'rangos',
  ];

  /** @type {AbortController | undefined} */
  #escuchas;

  /** @type {HTMLElement | null} */
  #copiaImpresion = null;

  #tituloOriginal = '';

  connectedCallback() {
    super.connectedCallback();
    // Al volver de enviar el informe, la confirmación queda a la vista aunque el resultado ya no esté.
    const { envio } = this.refs;
    if (envio && new URL(window.location.href).searchParams.has('contact_posted')) envio.hidden = false;

    // También con Ctrl + P: si ya hay resultado, se imprime el informe y no la página.
    this.#escuchas?.abort();
    this.#escuchas = new AbortController();
    const { signal } = this.#escuchas;
    window.addEventListener('beforeprint', () => this.#prepararImpresion(), { signal });
    window.addEventListener('afterprint', () => this.#terminarImpresion(), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#escuchas?.abort();
    this.#terminarImpresion();
  }

  /** Abre el diálogo de impresión para guardar el informe en PDF. */
  imprimir() {
    this.#prepararImpresion();
    window.print();
  }

  /**
   * Actualiza el conteo de respuestas y quita el aviso de las preguntas ya respondidas.
   * (El framework entrega el formulario como event.target, así que se revisan todos los grupos.)
   * @param {Event} _event
   */
  actualizar(_event) {
    const grupos = this.#grupos();
    let respondidas = 0;

    for (const grupo of grupos) {
      if (this.#valor(grupo) === null) continue;
      respondidas += 1;
      if (grupo.hasAttribute('data-falta')) this.#marcarFalta(grupo, false);
    }

    this.refs.progreso.textContent = `${respondidas} de ${grupos.length} respondidas`;
    if (respondidas === grupos.length) this.refs.aviso.hidden = true;
  }

  /**
   * Valida, calcula el puntaje y muestra el resultado.
   * @param {SubmitEvent} event
   */
  calcular(event) {
    event.preventDefault();

    const grupos = this.#grupos();
    if (grupos.length === 0) return;

    const pendientes = grupos.filter((grupo) => this.#valor(grupo) === null);
    for (const grupo of grupos) this.#marcarFalta(grupo, pendientes.includes(grupo));

    const { aviso } = this.refs;
    const [primera] = pendientes;
    if (primera) {
      aviso.textContent =
        pendientes.length === 1
          ? 'Falta una pregunta por responder.'
          : `Faltan ${pendientes.length} preguntas por responder.`;
      aviso.hidden = false;
      primera.querySelector('input')?.focus();
      return;
    }
    aviso.hidden = true;

    /** @type {Respuesta[]} */
    const respuestas = grupos.map((grupo) => ({
      area: grupo.dataset.area || grupo.querySelector('legend')?.textContent?.trim() || '',
      valor: /** @type {number} */ (this.#valor(grupo)),
      texto: grupo.querySelector('input[type="radio"]:checked')?.closest('label')?.textContent?.trim() ?? '',
      lectura: grupo.dataset.lectura,
      lecturaTexto: grupo.dataset.lecturaTexto,
    }));
    const total = respuestas.reduce((suma, respuesta) => suma + respuesta.valor, 0);
    const puntaje = Math.round((total / (respuestas.length * PUNTOS_MAXIMOS)) * 100);

    this.#mostrarResultado(puntaje, respuestas);
    publicarEvento('autoevaluacion_completada', { puntaje });
  }

  /** Limpia las respuestas y vuelve a la primera pregunta. */
  reiniciar() {
    const { formulario, resultado, medidor, progreso } = this.refs;
    formulario.reset();
    for (const grupo of this.#grupos()) this.#marcarFalta(grupo, false);
    resultado.hidden = true;
    if (this.refs.envio) this.refs.envio.hidden = true;
    medidor.querySelector('svg')?.style.setProperty('--oj-p', '0');
    progreso.textContent = `0 de ${this.#grupos().length} respondidas`;
    formulario.querySelector('input')?.focus();
  }

  /**
   * @param {number} puntaje
   * @param {Respuesta[]} respuestas
   */
  #mostrarResultado(puntaje, respuestas) {
    const { resultado, medidor, puntaje: puntajeRef, resultadoTitulo, resultadoTexto, prioridades, cta } = this.refs;
    const rangos = this.#rangos();
    const rango = puntaje < 40 ? rangos.bajo : puntaje < 70 ? rangos.medio : rangos.alto;

    puntajeRef.textContent = String(puntaje);
    resultadoTitulo.textContent = rango?.titulo ?? '';
    resultadoTexto.textContent = rango?.texto ?? '';

    const debiles = respuestas
      .filter((respuesta) => respuesta.valor <= 1)
      .sort((a, b) => a.valor - b.valor)
      .slice(0, 3);

    prioridades.replaceChildren(
      ...(debiles.length > 0 ? debiles.map((respuesta) => respuesta.area) : [rangos.sinPrioridades ?? '']).map(
        (texto) => {
          const elemento = document.createElement('li');
          elemento.textContent = texto;
          return elemento;
        }
      )
    );

    const destino = new URL(this.dataset.cta || '/pages/contacto', window.location.origin);
    destino.searchParams.set('puntaje', String(puntaje));
    if (debiles.length > 0) destino.searchParams.set('areas', debiles.map((respuesta) => respuesta.area).join(', '));
    cta.href = `${destino.pathname}${destino.search}`;

    const { envio } = this.refs;
    if (envio) {
      const campo = envio.querySelector('[data-oj-envio-resumen]');
      if (campo instanceof HTMLInputElement) {
        const detalle = respuestas.map((respuesta) => `${respuesta.area}: ${respuesta.valor}/${PUNTOS_MAXIMOS}`).join('; ');
        const areas = debiles.length > 0 ? debiles.map((respuesta) => respuesta.area).join(', ') : 'ninguna en rojo';
        campo.value = `Puntaje ${puntaje}/100 (${rango?.titulo ?? ''}). Áreas por mejorar: ${areas}. Respuestas: ${detalle}.`;
      }
      envio.hidden = false;
    }

    this.#llenarInforme(puntaje, rango, respuestas, debiles, rangos.sinPrioridades ?? '');

    resultado.hidden = false;
    const orbita = medidor.querySelector('svg');
    orbita?.style.setProperty('--oj-p', '0');
    // Un cuadro después, para que la transición de --oj-p arranque desde 0.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => orbita?.style.setProperty('--oj-p', String(puntaje)));
    });

    resultado.focus();
    resultado.scrollIntoView({ block: 'start', behavior: this.#movimientoReducido() ? 'auto' : 'smooth' });
  }

  /**
   * Llena el informe de una hoja: puntaje y escala, cada área con su respuesta y nivel, y las
   * áreas débiles con la lectura recomendada.
   * @param {number} puntaje
   * @param {{ titulo: string, texto: string } | undefined} rango
   * @param {Respuesta[]} respuestas
   * @param {Respuesta[]} debiles
   * @param {string} sinPrioridades
   */
  #llenarInforme(puntaje, rango, respuestas, debiles, sinPrioridades) {
    const { informeFecha, informePuntaje, informeTitulo, informeTexto, informeEscala, informeAreas, informePrioridades } =
      this.refs;
    if (!informeAreas || !informePrioridades) return;

    if (informeFecha) {
      informeFecha.textContent = new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
    }
    if (informePuntaje) informePuntaje.textContent = String(puntaje);
    if (informeTitulo) informeTitulo.textContent = rango?.titulo ?? '';
    if (informeTexto) informeTexto.textContent = rango?.texto ?? '';
    informeEscala?.style.setProperty('--oj-p', String(puntaje));

    informeAreas.replaceChildren(
      ...respuestas.map((respuesta) => {
        const fila = document.createElement('tr');
        if (debiles.includes(respuesta)) fila.classList.add('oj-informe-pdf__fila--debil');

        const area = document.createElement('th');
        area.scope = 'row';
        area.textContent = respuesta.area;

        const texto = document.createElement('td');
        texto.textContent = respuesta.texto;

        const nivel = document.createElement('td');
        const barras = document.createElement('span');
        barras.className = 'oj-informe-pdf__nivel';
        barras.setAttribute('aria-hidden', 'true');
        for (let i = 0; i < PUNTOS_MAXIMOS; i += 1) {
          const barra = document.createElement('i');
          if (i < respuesta.valor) barra.className = 'oj-informe-pdf__nivel-lleno';
          barras.append(barra);
        }
        nivel.append(barras, ` ${respuesta.valor} de ${PUNTOS_MAXIMOS}`);

        fila.append(area, texto, nivel);
        return fila;
      })
    );

    if (debiles.length === 0) {
      const elemento = document.createElement('li');
      elemento.className = 'oj-informe-pdf__sin-prioridades';
      elemento.textContent = sinPrioridades;
      informePrioridades.replaceChildren(elemento);
      return;
    }

    informePrioridades.replaceChildren(
      ...debiles.map((respuesta) => {
        const elemento = document.createElement('li');
        const area = document.createElement('strong');
        area.textContent = respuesta.area;
        elemento.append(area);

        if (respuesta.lectura && respuesta.lecturaTexto) {
          const destino = new URL(respuesta.lectura, window.location.origin);
          const lectura = document.createElement('p');
          const enlace = document.createElement('a');
          enlace.href = destino.href;
          enlace.textContent = `«${respuesta.lecturaTexto}»`;
          const direccion = document.createElement('span');
          direccion.className = 'oj-informe-pdf__url';
          direccion.textContent = `${destino.host}${destino.pathname}`;
          lectura.append('Para empezar: ', enlace, direccion);
          elemento.append(lectura);
        }
        return elemento;
      })
    );
  }

  /** Pone una copia del informe directo en body para que la hoja de impresión oculte todo lo demás. */
  #prepararImpresion() {
    const { resultado, informe, puntaje } = this.refs;
    if (!informe || resultado.hidden) return;

    this.#copiaImpresion?.remove();
    const copia = /** @type {HTMLElement} */ (informe.cloneNode(true));
    copia.hidden = false;
    copia.classList.add('oj-informe-pdf--impresion');
    for (const elemento of copia.querySelectorAll('[ref]')) elemento.removeAttribute('ref');
    copia.removeAttribute('ref');
    document.body.append(copia);
    this.#copiaImpresion = copia;

    document.documentElement.classList.add(CLASE_IMPRESION);
    // El navegador propone el título como nombre del PDF.
    if (!this.#tituloOriginal) this.#tituloOriginal = document.title;
    document.title = `Quilla · Tu autoevaluación: ${puntaje.textContent} de 100`;
  }

  #terminarImpresion() {
    this.#copiaImpresion?.remove();
    this.#copiaImpresion = null;
    document.documentElement.classList.remove(CLASE_IMPRESION);
    if (this.#tituloOriginal) {
      document.title = this.#tituloOriginal;
      this.#tituloOriginal = '';
    }
  }

  /** @returns {HTMLFieldSetElement[]} */
  #grupos() {
    return this.refs.grupos ?? [];
  }

  /**
   * @param {HTMLFieldSetElement} grupo
   * @returns {number | null}
   */
  #valor(grupo) {
    const elegido = grupo.querySelector('input[type="radio"]:checked');
    if (!(elegido instanceof HTMLInputElement)) return null;
    const valor = Number(elegido.value);
    return Number.isFinite(valor) ? Math.min(Math.max(valor, 0), PUNTOS_MAXIMOS) : null;
  }

  /**
   * @param {HTMLFieldSetElement} grupo
   * @param {boolean} falta
   */
  #marcarFalta(grupo, falta) {
    grupo.toggleAttribute('data-falta', falta);
    const mensaje = grupo.querySelector('.oj-auto__falta');
    if (mensaje instanceof HTMLElement) mensaje.hidden = !falta;
    for (const opcion of grupo.querySelectorAll('input[type="radio"]')) {
      if (falta) opcion.setAttribute('aria-invalid', 'true');
      else opcion.removeAttribute('aria-invalid');
    }
  }

  /** @returns {{ bajo?: { titulo: string, texto: string }, medio?: { titulo: string, texto: string }, alto?: { titulo: string, texto: string }, sinPrioridades?: string }} */
  #rangos() {
    try {
      return JSON.parse(this.refs.rangos.textContent ?? '{}');
    } catch {
      return {};
    }
  }

  #movimientoReducido() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('oj-autoevaluacion-component')) {
  customElements.define('oj-autoevaluacion-component', OjAutoevaluacionComponent);
}
